import { useState, useEffect } from 'react';

/**
 * Custom hook to detect the average brightness of an image at its bottom portion.
 * Useful for adapting UI contrast (e.g. taskbar) based on the background image.
 * 
 * @param imageUrl - The source URL of the image to analyze
 * @returns 'light' | 'dark' depending on the detected brightness
 */
export const useImageBrightness = (imageUrl: string | undefined): 'light' | 'dark' => {
    const [contrast, setContrast] = useState<'light' | 'dark'>('dark'); // Default to dark for light text (safe default)

    useEffect(() => {
        if (!imageUrl) return;

        const img = new Image();
        img.crossOrigin = "Anonymous"; // Try to handle CORS if image is on a different domain
        img.src = imageUrl;

        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) return;

                // We only care about the bottom 20% of the image where the taskbar usually sits
                const sampleHeight = img.height * 0.2;
                const startY = img.height - sampleHeight;

                // Scale down significantly for performance
                canvas.width = 100;
                canvas.height = 20;

                ctx.drawImage(img, 0, startY, img.width, sampleHeight, 0, 0, canvas.width, canvas.height);

                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
                let r = 0, g = 0, b = 0;

                for (let i = 0; i < imageData.length; i += 4) {
                    r += imageData[i];
                    g += imageData[i + 1];
                    b += imageData[i + 2];
                }

                const pixelsCount = imageData.length / 4;
                const avgR = r / pixelsCount;
                const avgG = g / pixelsCount;
                const avgB = b / pixelsCount;

                // Calculate perceived luminance (ITU-R BT.709)
                const luminance = (0.2126 * avgR + 0.7152 * avgG + 0.0722 * avgB) / 255;

                // If luminance > 0.6, the background is light, so use dark contrast
                setContrast(luminance > 0.6 ? 'light' : 'dark');
            } catch (error) {
                console.error("Error analyzing image brightness:", error);
                setContrast('dark'); // Fallback to dark (white text)
            }
        };

        img.onerror = () => {
            console.warn("Could not load image for brightness analysis:", imageUrl);
            setContrast('dark');
        };
    }, [imageUrl]);

    return contrast;
};
